import User from "@mapmycustomers/shared/types/User";
import getSvgForCircleWithText from "@mapmycustomers/shared/util/svg/getSvgForCircleWithText";
import { getAvatarSvg } from "@mapmycustomers/ui";

import { UserColorClassNameGetter } from "@app/store/userColor/selectors";
import colors from "@app/styles/_colors.module.scss";
import Cluster from "@app/util/clustering/types/Cluster";
import { BASE_CLUSTER_ZINDEX } from "@app/util/map/consts";
import isUserActive from "@app/util/map/isUserActive";
import getScaledSize from "@app/util/map/markerStyles/getScaledSize";
import svgToDataUrl from "@app/util/map/svgToDataUrl";

const CLUSTER_SIZE = 100;
const SCALE = 0.8;
const AVATAR_SIZE = 36;
const AVATAR_OFFSET = 3; // offset between avatars

const getUserSvg = (
  user: User,
  getUserColor: UserColorClassNameGetter,
  userIdToBase64Avatar: Map<User["id"], string>
) =>
  getAvatarSvg(
    {
      greyed: !isUserActive(user),
      size: AVATAR_SIZE,
      user: user,
      userImageString: userIdToBase64Avatar?.get(user.id),
    },
    getUserColor
  );

const getUserLocationClusterStyles = (
  cluster: Cluster<User>,
  getUserColor: UserColorClassNameGetter,
  userIdToBase64Avatar: Map<User["id"], string>
): google.maps.Data.StyleOptions => {
  // we are pretty sure there are at least two users in this cluster
  // because otherwise it would be displayed as a single user avatar
  // as for the third one, it can be undefined
  // we sort items to pick the ones with an avatar to the front, so
  // that cluster preview is nicer
  const [user1, user2, user3] = [...cluster.items].sort(
    (a, b) => Number(userIdToBase64Avatar.has(b.id)) - Number(userIdToBase64Avatar.has(a.id))
  );

  const avatar1Svg = getUserSvg(user1, getUserColor, userIdToBase64Avatar);
  const avatar2Svg = getUserSvg(user2, getUserColor, userIdToBase64Avatar);
  let avatar3Svg = undefined;

  // these are x coordinates used to arrange 2 or 3 avatars inside the cluster circle
  // y coordinate is the same for all of them
  let x1, x2, x3;
  const y = CLUSTER_SIZE / 2 - AVATAR_SIZE / 2;

  if (cluster.count === 2) {
    // if only two avatars to show, we put them to the either side of the center
    x1 = CLUSTER_SIZE / 2 - AVATAR_SIZE - AVATAR_OFFSET;
    x2 = CLUSTER_SIZE / 2 + AVATAR_OFFSET;
  } else {
    // if three avatars to show, we put them to the either side of the center, plus one right in the center

    // also, we populate avatar3Svg with either a user avatar (if there were exactly 3 users in the
    // cluster), or with a number of remaining users if there were more than 3
    if (cluster.count === 3) {
      avatar3Svg = getUserSvg(user3, getUserColor, userIdToBase64Avatar);
    } else {
      avatar3Svg = getSvgForCircleWithText({
        fillColor: colors.softSlate,
        size: AVATAR_SIZE,
        strokeColor: colors.white,
        // subtracting 2 because two avatars are already displayed
        text: `+${cluster.count - 2}`,
        textColor: colors.white,
      });
    }

    x1 = CLUSTER_SIZE / 2 - AVATAR_SIZE - AVATAR_OFFSET;
    x2 = CLUSTER_SIZE / 2 - AVATAR_SIZE / 2;
    x3 = CLUSTER_SIZE / 2 + AVATAR_OFFSET;
  }

  // resulting cluster SVG: circle for the cluster itself + 2 or 3 avatars inside
  const clusterSvg = `<svg width="${CLUSTER_SIZE}" height="${CLUSTER_SIZE}" viewBox="0 0 ${CLUSTER_SIZE} ${CLUSTER_SIZE}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g>
        <circle cx="${CLUSTER_SIZE / 2}" cy="${CLUSTER_SIZE / 2}" r="${
    CLUSTER_SIZE / 2 - 3
  }" fill="${colors.primary010}" stroke="${colors.primary100}" stroke-width="6"/>
        <g transform="translate(${x1}, ${y})">${avatar1Svg}</g>
        <g transform="translate(${x2}, ${y})">${avatar2Svg}</g>
        ${avatar3Svg ? `<g transform="translate(${x3}, ${y})">${avatar3Svg}</g>` : ""}
      </g>
    </svg>`;

  return {
    clickable: true,
    icon: {
      anchor: new google.maps.Point(...getScaledSize([50, 50], SCALE)),
      scaledSize: new google.maps.Size(...getScaledSize([100, 100], SCALE)),
      url: svgToDataUrl(clusterSvg),
    },
    opacity: 0.85,
    // adjust zIndex to be above other markers
    zIndex: BASE_CLUSTER_ZINDEX + cluster.count,
  };
};

export default getUserLocationClusterStyles;
